/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * Cihaz: uetds + uetdsCredentialId. UETDS kimlik listesi:
 * GET /api/devices/{id}/uetds-credential-options (panel; firma eşlemesi sunucuda).
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  HStack,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Spinner,
  Box,
  Divider,
  Badge,
  Select,
  useToast,
} from "@chakra-ui/react";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";

function fmtTs(value) {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("tr-TR");
}

function statusColor(st) {
  const u = String(st || "").toUpperCase();
  if (u === "SUCCESS") return "green";
  if (u === "FAILED") return "red";
  if (u === "SKIPPED") return "orange";
  return "gray";
}

export default function UetdsKonumBildirModal({ isOpen, onClose, arac }) {
  const toast = useToast();
  const { handleError } = useErrorHandler();
  const deviceId = arac?.id;
  const plaka = arac?.plaka?.trim?.() || arac?.plaka || `#${deviceId}`;

  const [loading, setLoading] = useState(false);
  const [togglePending, setTogglePending] = useState(false);
  const [credentialSwitchPending, setCredentialSwitchPending] = useState(false);

  const [uetdsEnabled, setUetdsEnabled] = useState(false);
  const [linkedUsersCount, setLinkedUsersCount] = useState(0);
  const [ownerUserId, setOwnerUserId] = useState(null);
  const [ownerResolutionNote, setOwnerResolutionNote] = useState("");
  const [credentials, setCredentials] = useState([]);
  const [selectedCredentialId, setSelectedCredentialId] = useState("");
  const [deviceCredentialId, setDeviceCredentialId] = useState(null);

  const [lastStatus, setLastStatus] = useState("");
  const [lastDetail, setLastDetail] = useState("");
  const [lastAttemptAt, setLastAttemptAt] = useState("");
  const [lastSentAt, setLastSentAt] = useState("");

  const fetchSnapshot = useCallback(async () => {
    if (deviceId == null) return;
    setLoading(true);
    try {
      const [deviceRes, optRes] = await Promise.all([
        api.get(`/api/devices/${deviceId}/traccar`),
        api.get(`/api/devices/${deviceId}/uetds-credential-options`),
      ]);
      const d = await deviceRes.json();
      const opt = await optRes.json();

      setLinkedUsersCount(Number(opt.linkedUsersCount) || 0);
      setOwnerUserId(opt.ownerUserId ?? null);
      setOwnerResolutionNote(typeof opt.warning === "string" && opt.warning.trim() ? opt.warning : "");

      const credList = Array.isArray(opt.uetdsCredentials) ? opt.uetdsCredentials : [];
      setCredentials(credList);

      setUetdsEnabled(Boolean(d.uetds));
      setDeviceCredentialId(d.uetdsCredentialId ?? null);

      const currentDevCred = d.uetdsCredentialId;
      let pick = "";
      if (credList.some((c) => c?.id === currentDevCred)) {
        pick = String(currentDevCred);
      } else if (credList.length === 1 && credList[0]?.id != null) {
        pick = String(credList[0].id);
      } else if (credList[0]?.id != null) {
        pick = String(credList[0].id);
      }

      const safePick =
        pick ||
        (credList[0]?.id != null ? String(credList[0].id) : "");
      setSelectedCredentialId(safePick);

      setLastStatus(d.uetdsKonumLastStatus ?? "");
      setLastDetail(typeof d.uetdsKonumLastDetail === "string" ? d.uetdsKonumLastDetail : "");
      setLastAttemptAt(d.uetdsKonumLastAttemptAt ?? "");
      setLastSentAt(d.uetdsKonumLastSentAt ?? "");
    } catch (e) {
      handleError(e, "UETDS konum bilgisi");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleError
  }, [deviceId]);

  useEffect(() => {
    if (isOpen && deviceId != null) {
      fetchSnapshot();
    }
  }, [isOpen, deviceId, fetchSnapshot]);

  const putDeviceUetds = async (payload) => {
    const res = await api.put(`/api/devices/${deviceId}`, payload);
    return res.json();
  };

  const handleToggle = async (next) => {
    if (deviceId == null) return;

    if (next) {
      const cid = Number(selectedCredentialId);
      if (!credentials.length) {
        toast({
          title: "UETDS hesabı yok",
          description: "Önce ilgili firma için Hesaplar → UETDS ayarlarından en az bir hesap tanımlayın.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      if (!Number.isFinite(cid)) {
        toast({
          title: "Seçim gerekli",
          description: "Konum bildirimini açmadan önce kullanılacak UETDS hesabını seçin.",
          status: "warning",
          duration: 4500,
          isClosable: true,
        });
        return;
      }
    }

    setTogglePending(true);
    try {
      const body = next
        ? { uetds: true, uetdsCredentialId: Number(selectedCredentialId) }
        : { uetds: false };
      await putDeviceUetds(body);
      setUetdsEnabled(next);
      toast({
        title: next ? "Konum bildirimi açıldı" : "Konum bildirimi kapatıldı",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      await fetchSnapshot();
    } catch (e) {
      handleError(e, "UETDS konum bildirimi");
    } finally {
      setTogglePending(false);
    }
  };

  const handleCredentialChange = async (nextIdStr) => {
    setSelectedCredentialId(nextIdStr);
    if (!uetdsEnabled || deviceId == null) return;

    const cid = Number(nextIdStr);
    if (!Number.isFinite(cid)) return;

    if (credentialSwitchPending || togglePending) return;
    if (cid === Number(deviceCredentialId)) return;

    setCredentialSwitchPending(true);
    try {
      await putDeviceUetds({ uetds: true, uetdsCredentialId: cid });
      toast({
        title: "UETDS hesabı güncellendi",
        description: "Bu araç seçilen kimlik ile zamanlayıcıya devam edecek.",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
      await fetchSnapshot();
    } catch (e) {
      handleError(e, "UETDS hesap seçimi");
    } finally {
      setCredentialSwitchPending(false);
    }
  };

  const closeIfAllowed = () => {
    if (!togglePending && !loading && !credentialSwitchPending) onClose?.();
  };

  const st = lastStatus.trim();
  const hasStatus = Boolean(st);
  const hasLinkedUsers = linkedUsersCount > 0;
  const hasOwner = ownerUserId != null;

  return (
    <Modal isOpen={isOpen} onClose={closeIfAllowed} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="md" pb={1}>
          UETDS konum bildirimi — {plaka}
        </ModalHeader>
        <ModalCloseButton isDisabled={togglePending || credentialSwitchPending || loading} />
        <ModalBody pb={3}>
          {loading ? (
            <HStack py={10} justify="center">
              <Spinner />
            </HStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              {!hasLinkedUsers ? (
                <Text fontSize="sm" color="red.600">
                  Bu cihaza bağlı Traccar kullanıcısı bulunamadı (<code>?deviceId=</code>). Konum bildirimi
                  için kullanıcı–cihaz eşlemesini kontrol edin.
                </Text>
              ) : (
                <>
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="gray.700" mb={1}>
                      UETDS kimliği (bu araç için)
                    </Text>
                    <Text fontSize="xs" color="gray.600" mb={2}>
                      Seçilen satırın <strong>id</strong> değeri cihaza <code>uetdsCredentialId</code> olarak
                      yazılır; hesapların sahibi bu cihaza Traccar’da bağlı olmalıdır.
                    </Text>
                    {ownerResolutionNote ? (
                      <Text fontSize="xs" color="orange.700" mb={2}>
                        {ownerResolutionNote}
                      </Text>
                    ) : null}
                    <FormControl>
                      <FormLabel fontSize="sm">Firma UETDS hesabı</FormLabel>
                      <Select
                        size="sm"
                        value={selectedCredentialId}
                        onChange={(e) => handleCredentialChange(e.target.value)}
                        isDisabled={
                          togglePending ||
                          credentialSwitchPending ||
                          !hasOwner ||
                          !credentials.length
                        }
                      >
                        {credentials.length === 0 ? (
                          <option value="">— Firma kullanıcısında UETDS hesabı yok —</option>
                        ) : (
                          credentials.map((c) => (
                            <option key={c.id} value={String(c.id)}>
                              {(c.username != null ? String(c.username).trim() : "") || `#${c.id}`}
                            </option>
                          ))
                        )}
                      </Select>
                    </FormControl>
                  </Box>

                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel htmlFor="uetds-device-sw" mb="0" fontSize="sm">
                      Periyodik konum bildirimi (liste)
                    </FormLabel>
                    <Switch
                      id="uetds-device-sw"
                      colorScheme="blue"
                      isChecked={uetdsEnabled}
                      onChange={(e) => handleToggle(e.target.checked)}
                      isDisabled={
                        togglePending ||
                        credentialSwitchPending ||
                        !credentials.length ||
                        !hasOwner
                      }
                    />
                  </FormControl>
                  {credentials.length === 0 && hasOwner ? (
                    <Text fontSize="xs" color="orange.700">
                      Bu firmanın kullanıcısında UETDS kaydı tanımlı değil. Hesaplar → UETDS ayarlarından hesap ekleyin.
                    </Text>
                  ) : null}
                </>
              )}

              <Text fontSize="xs" color="gray.600">
                Açıldığında sunucudaki zamanlayıcı son kayıtlı konumu seçilen UETDS kullanıcı adıyla iletir.
              </Text>

              <Divider />

              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.700" mb={2}>
                  Son zamanlayıcı denemesi
                </Text>
                {deviceCredentialId != null && uetdsEnabled ? (
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    Cihazdaki seçili kimlik id: <Badge fontSize="0.65rem">{String(deviceCredentialId)}</Badge>
                  </Text>
                ) : null}
                {!hasStatus && !lastDetail ? (
                  <Text fontSize="sm" color="gray.500">
                    Henüz kayıtlı bir deneme özeti yok veya zamanlayıcı henüz çalışmadı.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    <HStack flexWrap="wrap" spacing={2}>
                      <Text fontSize="xs" color="gray.600">
                        Durum:
                      </Text>
                      {hasStatus ? (
                        <Badge colorScheme={statusColor(st)} variant="solid" fontSize="0.65rem">
                          {st}
                        </Badge>
                      ) : (
                        <Text fontSize="sm">—</Text>
                      )}
                    </HStack>
                    {(lastDetail || "").trim() ? (
                      <Box
                        fontSize="sm"
                        px={3}
                        py={2}
                        borderRadius="md"
                        bg={
                          String(st).toUpperCase() === "SUCCESS"
                            ? "green.50"
                            : String(st).toUpperCase() === "FAILED"
                              ? "red.50"
                              : String(st).toUpperCase() === "SKIPPED"
                                ? "orange.50"
                                : "gray.50"
                        }
                        borderWidth="1px"
                        borderColor="gray.200"
                        wordBreak="break-word"
                      >
                        {lastDetail}
                      </Box>
                    ) : null}
                    <Text fontSize="xs" color="gray.600">
                      Son deneme: {fmtTs(lastAttemptAt)}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Son başarılı gönderim: {fmtTs(lastSentAt)}
                    </Text>
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={closeIfAllowed} isDisabled={togglePending || credentialSwitchPending}>
            Kapat
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchSnapshot()}
            isLoading={loading}
            isDisabled={togglePending || credentialSwitchPending || deviceId == null}
          >
            Yenile
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
